import type { AuthConfigResponse } from "@central-hub/contracts";
import { UserManager, WebStorageStateStore } from "oidc-client-ts";
import { storage } from "./storage";

const TOKEN_KEY = "central-hub.api-token";

export interface Session {
  kind: "oidc" | "token";
  /** A currently valid access token, refreshed when needed; null when the session is over. */
  getToken(): Promise<string | null>;
  /** After a 401: try once to get a fresh token. */
  refresh(): Promise<boolean>;
  signOut(): Promise<void>;
}

const isRedirectCallback = () => {
  const params = new URLSearchParams(window.location.search);
  return params.has("state") && (params.has("code") || params.has("error"));
};

/**
 * Sign-in for the panel: OIDC (authorization code + PKCE, refresh tokens so a wall display
 * stays signed in) when the hub has a provider configured, or a pasted API token otherwise
 * (local setups, break-glass when the provider is down).
 */
export class Auth {
  private readonly manager: UserManager | null;

  constructor(readonly config: AuthConfigResponse) {
    const oidc = config.oidc;
    this.manager = oidc
      ? new UserManager({
          authority: oidc.issuer,
          client_id: oidc.clientId,
          redirect_uri: `${window.location.origin}/`,
          post_logout_redirect_uri: `${window.location.origin}/`,
          response_type: "code",
          scope: oidc.scopes,
          ...(oidc.audience !== undefined && { extraQueryParams: { audience: oidc.audience } }),
          userStore: new WebStorageStateStore({ store: window.localStorage }),
          automaticSilentRenew: true,
          loadUserInfo: false,
        })
      : null;
  }

  get oidcEnabled(): boolean {
    return this.manager !== null;
  }

  /** Completes a redirect sign-in, or picks up an existing session. */
  async restore(): Promise<Session | null> {
    const manager = this.manager;
    if (manager && isRedirectCallback()) {
      try {
        await manager.signinRedirectCallback();
      } finally {
        window.history.replaceState(null, "", window.location.pathname);
      }
    }
    if (manager) {
      const user = await manager.getUser();
      if (user && (!user.expired || (await this.renew()))) return this.oidcSession();
    }
    const token = storage.get(TOKEN_KEY);
    return token ? this.tokenSession(token) : null;
  }

  async signIn(): Promise<void> {
    await this.manager?.signinRedirect();
  }

  signInWithToken(token: string): Session {
    storage.set(TOKEN_KEY, token);
    return this.tokenSession(token);
  }

  private async renew(): Promise<boolean> {
    try {
      return (await this.manager?.signinSilent()) != null;
    } catch {
      return false;
    }
  }

  private oidcSession(): Session {
    const manager = this.manager as UserManager;
    return {
      kind: "oidc",
      getToken: async () => {
        const user = await manager.getUser();
        if (user && !user.expired) return user.access_token;
        return (await this.renew()) ? ((await manager.getUser())?.access_token ?? null) : null;
      },
      refresh: () => this.renew(),
      signOut: async () => {
        await manager.removeUser();
        try {
          await manager.signoutRedirect(); // leaves the page when the provider supports it
        } catch {
          window.location.reload();
        }
      },
    };
  }

  private tokenSession(token: string): Session {
    return {
      kind: "token",
      getToken: async () => token,
      refresh: async () => false,
      signOut: async () => {
        storage.remove(TOKEN_KEY);
      },
    };
  }
}
